import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../App';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, ArrowRight, AlertTriangle, Shield, Mail, Check,
  User, Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * YOUTH ADULT CONVERSION (PART C — FINAL LOCKED DIRECTIVE)
 * 
 * Youth Adult → Adult conversion:
 * - Parent approval flow
 * - New email requirement
 * - Conversion to Adult Free or Paid
 */

export default function YouthAdultConversion() {
  const navigate = useNavigate();
  const { user, refreshUser } = useContext(AuthContext);
  
  const [step, setStep] = useState(1);
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    // Check if user is eligible (16-17 or already a youth adult)
    if (user.account_type !== 'youth_adult' && user.age < 16) {
      toast.error('You must be 16+ to request adult conversion');
      navigate('/myworld');
    }
  }, [user, navigate]);

  const handleSubmitRequest = async () => {
    if (newEmail !== confirmEmail) {
      toast.error('Emails do not match');
      return;
    }
    if (!newEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    if (!acknowledged) {
      toast.error('Please acknowledge the terms');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/family/youth-to-adult/request`, {
        new_email: newEmail
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRequestSent(true);
      toast.success('Request sent to your parent');
    } catch (error) {
      console.error('Failed to submit request:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  if (requestSent) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <Card className="max-w-md mx-4 p-8 text-center" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'var(--lk-sage-muted)' }}>
            <Check className="w-8 h-8" style={{ color: 'var(--lk-primary-green)' }} />
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--fy-text-primary)' }}>Request Sent!</h2>
          <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            Your parent will receive a notification to approve your conversion to an adult account.
          </p>
          <p className="text-sm mb-6" style={{ color: 'var(--fy-text-secondary)' }}>
            Once approved, you&apos;ll receive a confirmation email at <strong>{newEmail}</strong> with instructions to complete the process.
          </p>
          <Button onClick={() => navigate('/myworld')} className="rounded-full" style={{ backgroundColor: 'var(--lk-primary-green)', color: 'white' }}>
            Back to Base Camp
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center h-14">
            <button onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-black/5">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <div className="flex-1 text-center">
              <h1 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Convert to Adult Account</h1>
              <p className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>Step {step} of 3</p>
            </div>
            <div className="w-9" />
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        
        {/* Step 1: Explanation */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'var(--lk-sage-muted)' }}>
                <User className="w-8 h-8" style={{ color: 'var(--lk-primary-green)' }} />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
                Ready to become an adult member?
              </h2>
              <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                This will convert your youth adult account to a full adult account.
              </p>
            </div>

            <Card className="p-6" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <h3 className="font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>What changes:</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2" style={{ color: 'var(--fy-text-secondary)' }}>
                  <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
                  <span>Full access to all adult features</span>
                </li>
                <li className="flex items-start gap-2" style={{ color: 'var(--fy-text-secondary)' }}>
                  <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
                  <span>Ability to create Commons and Groups</span>
                </li>
                <li className="flex items-start gap-2" style={{ color: 'var(--fy-text-secondary)' }}>
                  <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
                  <span>Parent visibility will be removed</span>
                </li>
                <li className="flex items-start gap-2" style={{ color: 'var(--fy-text-secondary)' }}>
                  <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
                  <span>Your own independent account</span>
                </li>
              </ul>
            </Card>

            <Card className="p-4" style={{ backgroundColor: 'rgba(217, 138, 58, 0.1)', border: '1px solid var(--lk-secondary-orange)' }}>
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--lk-secondary-orange)' }} />
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--lk-secondary-orange)' }}>Parent Approval Required</p>
                  <p className="text-sm mt-1" style={{ color: 'var(--fy-text-secondary)' }}>
                    Your parent must approve this conversion. They will receive a notification when you submit your request.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Step 2: New Email */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
                Set up your new email
              </h2>
              <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                Adult accounts require their own email address. This cannot be the same as your parent&apos;s email.
              </p>
            </div>

            <Card className="p-6" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--fy-text-primary)' }}>
                    Your new email address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--fy-text-disabled)' }} />
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-4 py-3 rounded-xl"
                      style={{ 
                        backgroundColor: 'var(--fy-surface-muted)', 
                        border: '1px solid var(--fy-border-subtle)',
                        color: 'var(--fy-text-primary)'
                      }}
                      data-testid="new-email-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--fy-text-primary)' }}>
                    Confirm email address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--fy-text-disabled)' }} />
                    <input
                      type="email"
                      value={confirmEmail}
                      onChange={(e) => setConfirmEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-4 py-3 rounded-xl"
                      style={{ 
                        backgroundColor: 'var(--fy-surface-muted)', 
                        border: '1px solid var(--fy-border-subtle)',
                        color: 'var(--fy-text-primary)'
                      }}
                      data-testid="confirm-email-input"
                    />
                  </div>
                </div>
              </div>
            </Card>

            <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
              <p className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
                <Shield className="w-3 h-3 inline mr-1" />
                You&apos;ll receive a verification email at this address after your parent approves the conversion.
              </p>
            </div>
          </div>
        )}

        {/* Step 3: Acknowledgment */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
                Confirm &amp; request
              </h2>
              <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                Please read and acknowledge the following before submitting your request.
              </p>
            </div>

            <Card className="p-6" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <h3 className="font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>Summary</h3>
              <div className="space-y-2 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                <p><strong>New email:</strong> {newEmail}</p>
                <p><strong>Account type:</strong> Adult (Free)</p>
                <p><strong>Parent approval:</strong> Required</p>
              </div>
            </Card>

            <label className="flex items-start gap-3 p-4 rounded-xl cursor-pointer" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="mt-1"
                data-testid="acknowledge-conversion"
              />
              <span className="text-sm" style={{ color: 'var(--fy-text-primary)' }}>
                I understand that this conversion requires my parent&apos;s approval. Once converted, I will be responsible for my own account and parent visibility will be removed.
              </span>
            </label>

            <Card className="p-4" style={{ backgroundColor: 'var(--lk-sage-muted)', border: '1px solid var(--lk-primary-green)' }}>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--lk-primary-green)' }} />
                <p className="text-sm" style={{ color: 'var(--fy-text-primary)' }}>
                  Your parent will have 7 days to respond. If they don&apos;t respond, you can request again or wait until you turn 18.
                </p>
              </div>
            </Card>
          </div>
        )}

        {/* Navigation */}
        <div className="mt-8 flex gap-3">
          {step > 1 && (
            <Button
              onClick={() => setStep(step - 1)}
              variant="outline"
              className="flex-1 h-12 rounded-xl"
              style={{ borderColor: 'var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
          
          {step < 3 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={step === 2 && (!newEmail || !confirmEmail)}
              className="flex-1 h-12 rounded-xl"
              style={{ backgroundColor: 'var(--lk-primary-green)', color: 'white' }}
              data-testid="next-step-btn"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmitRequest}
              disabled={loading || !acknowledged || newEmail !== confirmEmail}
              className="flex-1 h-12 rounded-xl"
              style={{ 
                backgroundColor: acknowledged && newEmail === confirmEmail ? 'var(--lk-primary-green)' : 'var(--fy-surface-muted)',
                color: acknowledged && newEmail === confirmEmail ? 'white' : 'var(--fy-text-disabled)'
              }}
              data-testid="submit-request-btn"
            >
              {loading ? 'Submitting...' : 'Request Conversion'}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
